import { Button } from "@components/controls/Button"
import { Link, useRoute, useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "framer-motion"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { MembershipLevelItemInfoFragment, MembershipLevelSubscriptionProductFragment, MembershipLevelsFragment } from "../../../types.graphql"
import { useMembershipStatus } from "../hooks/useAuth"

type Props = {
  data: MembershipLevelsFragment
  intent: "join" | "upgrade"
  onChoose?: (plan: MembershipLevelItemInfoFragment, product?: MembershipLevelSubscriptionProductFragment) => void
  loggedIn?: boolean
}

export function ChoosePlan(props: Props) {
  const router = useRouter()
  const route = useRoute()
  const status = useMembershipStatus()

  const plans = useMemo(
    () =>
      props.data.miscSettings?.agdaSettings?.publicMembershipPlans
        ?.filter((plan) => !!plan)
        .map((plan) => ({
          ...plan,
          product: plan?.products.find((product) => product?.__typename === "SubscriptionProduct") as MembershipLevelSubscriptionProductFragment | undefined,
        })),
    [props.data]
  )

  useEffect(() => {
    if (route.query.plan) {
      const plan = plans?.find((plan) => plan.slug === route.query.plan)
      if (plan) {
        if (props.onChoose) {
          router.replaceQuery({})
          setTimeout(() => {
            props.onChoose!(plan, plan.product)
          }, 100)
        }
      }
    }
  }, [route.query.plan])

  const [hoverPlan, setHoverPlan] = useState<string | null>(null)

  return (
    <div>
      {plans?.map((plan) => {
        let action: ReactNode = null
        if (status.loading) {
          action = null
        } else if (status.hasAccess && !status.expiresSoon) {
          if (status.planSlug === plan.slug) {
            action = <Button href="/account/membership">Manage</Button>
          }
        } else if (props.onChoose) {
          action = <Button onClick={() => props.onChoose!(plan!, plan.product!)}>Choose</Button>
        } else {
          action = <Button href={`/join/?plan=${encodeURI(plan?.slug!)}`}>Choose</Button>
        }
        // if (plan.title === "Design Business") {
        //   action = (
        //     <Button disabled variant="light">
        //       Coming soon
        //     </Button>
        //   )
        // }
        return (
          <PlanItem
            key={plan.slug!}
            title={plan.title!}
            slug={plan.slug!}
            onHover={() => setHoverPlan(plan.slug)}
            hovering={hoverPlan === plan.slug}
            action={action}
            description={(plan.product as any)?.shortDescription}
            price={plan.product?.price!}
          />
        )
      })}
      {route.query.reason === "awards" && (
        <PlanItem
          key="free"
          title="Create a non-member account"
          slug={"free"}
          onHover={() => setHoverPlan("free")}
          hovering={hoverPlan === "free"}
          action={
            <Button variant="light" href="/awards/signup/">
              Create a free account
            </Button>
          }
          description="Create a free account to submit to the AGDA Awards. This account does not include membership benefits."
          price="Free"
        />
      )}
      {!props.loggedIn && (
        <div className="pt-6 md:pt-6 type-body-m-serif rich-wysiwyg">
          <p>
            Existing member? <Link href="/account/membership">Sign into your account</Link> to renew or upgrade.
          </p>
        </div>
      )}
    </div>
  )
}

function PlanItem(props: { title: string; slug: string; hovering: boolean; description: string; price: string; action: ReactNode; onHover: () => void }) {
  return (
    <div className="border-b py-4" key={props.slug} onPointerEnter={() => props.onHover()}>
      <div className="flex justify-between items-start">
        <h2 className="type-title-m-medium pb-2">{props.title}</h2>
        <div>{props.action}</div>
      </div>
      <AnimatePresence>
        {props.hovering && (
          <motion.div initial={{ height: 0, opacity: 0 }} exit={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="overflow-hidden">
            <div className="type-body-m-serif pb-3 max-w-[35em]" dangerouslySetInnerHTML={{ __html: props.description ?? "" }}></div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="type-title-m-light">{props.price}</div>
    </div>
  )
}
